/**
 * Accende il monitor errori del browser (Sickbay, via @sentry/vue).
 *
 * Il DSN NON e' nel bundle: lo chiede al proprio backend su
 * `GET /api/public/config` (vedi `sickbayRuntime.ts` e la dottrina in
 * `sickbay/docs/DSN-A-RUNTIME.md`, decisione Atlas 31/08/2026). Sostituisce
 * l'init vecchio verso sentry.io che leggeva `VITE_SENTRY_DSN` al build.
 *
 * Cosa arriva da dove:
 * - `dsn` e `environment`: dal BACKEND, cioe' dal `.env.production` di quella
 *   macchina. Mai una costante qui.
 * - `release`: dal BUILD. Identifica il CODICE che sta girando: versione + sha
 *   corto. Quello del backend e' solo il fallback.
 *
 * Non si aspetta: la chiamata parte prima del mount ma non lo blocca. Con
 * l'await ogni utente pagherebbe fino a 3 secondi di schermo bianco per un
 * monitor. Se la rotta non esiste (404) o non risponde, `agganciaMonitor` non
 * solleva, non scrive in console, e l'app parte identica. Offline (PWA): il
 * fetch fallisce, il canale resta spento, nessun errore.
 *
 * La libreria si carica con import dinamico: il chunk `vendor-sentry` (vedi
 * `vite.config.ts`) si scarica SOLO se l'installazione ha un DSN.
 *
 * Struttura copiata da `gleem-ops:/home/andre/wt-pit-sickbay-fe/frontend/src/core/monitor/avvia.ts`
 * (squadra Pit, 06/09/2026), adattata a Thermo.
 */
import type { App } from 'vue'
import type { ErrorEvent } from '@sentry/vue'
import { agganciaMonitor } from './sickbayRuntime'
import { redigiEvento } from './sickbayRedact'

/** Frammento URL che provoca un errore deliberato, per provare la catena. */
export const HASH_CRASH_TEST = '#sickbay-crash-test'

/**
 * `thermo-frontend@5.0.1+a29c8f7`. La versione viene da `VITE_APP_VERSION`, lo
 * sha da `git rev-parse` al build; se uno manca resta l'altro. Torna '' solo se
 * non c'e' nessuno dei due, e a quel punto vale il release del backend.
 */
export function componiRelease(versione: string, sha: string): string {
  const v = versione.trim()
  const s = sha.trim()
  if (!v && !s) return ''
  if (!v) return `thermo-frontend@${s}`
  return s ? `thermo-frontend@${v}+${s}` : `thermo-frontend@${v}`
}

/**
 * Rumore atteso, non bug del codice. Thermo e' gia' il produttore piu' rumoroso
 * della flotta (401 ricorrenti lato backend): il browser non deve raddoppiarlo.
 * Ogni voce ha il posto dove quel caso e' GIA' gestito.
 */
export const ERRORI_IGNORATI: Array<string | RegExp> = [
  // Token scaduto / logout: l'interceptor axios pulisce e manda al login
  // (`core/services/api.ts`). Il backend lo conta gia' come 401.
  /Request failed with status code 401/,
  // Rete dell'utente, non nostra: axios senza risposta, timeout, richiesta annullata.
  /^Network Error$/,
  /timeout of \d+ms exceeded/,
  /^canceled$/,
  /AbortError/,
  // Chunk vecchi dopo un deploy: il router fa gia' il recovery con reload
  // (`core/router/index.ts`, attemptChunkRecovery).
  /Failed to fetch dynamically imported module/,
  /Importing a module script failed/,
  /error loading dynamically imported module/,
  /Unable to preload CSS/,
  // Benigno e noto in tutti i browser.
  /ResizeObserver loop/,
]

/** Errori nati dentro estensioni del browser: non sono nostri. */
export const URL_NEGATE: RegExp[] = [
  /^chrome-extension:\/\//i,
  /^moz-extension:\/\//i,
  /^safari-(web-)?extension:\/\//i,
]

/**
 * Prova di fumo dal browser, senza toccare un dato: si apre l'app con
 * `#sickbay-crash-test` nell'URL e, SOLO se il monitor si e' acceso, parte un
 * errore non gestito con un messaggio inequivocabile. Nel motore deve comparire
 * nel progetto `thermo-frontend`, con la `release` del build.
 */
function crashTestSeRichiesto(): void {
  if (typeof window === 'undefined' || window.location.hash !== HASH_CRASH_TEST) return
  setTimeout(() => {
    throw new Error('Sickbay crash-test ThermoGleem: evento deliberato, da ignorare')
  }, 0)
}

export function avviaMonitor(app: App): Promise<boolean> {
  return agganciaMonitor({
    init: (config) => {
      import('@sentry/vue')
        .then((Sentry) => {
          Sentry.init({
            app,
            dsn: config.sickbay_dsn ?? undefined,
            // Vuoto dal backend = non lo sappiamo. Non si inventa "production".
            environment: (config.environment ?? '').trim() || 'unknown',
            release: componiRelease(__APP_VERSION__, __GIT_SHA__) || config.release || undefined,
            // Nessun dato personale: pratiche GSE/Conto Termico di clienti veri.
            // Niente IP, niente utente, niente props dei componenti (portano
            // anagrafiche intere).
            sendDefaultPii: false,
            attachProps: false,
            // Solo errori. Niente tracce (era 0.2 verso sentry.io), niente replay.
            tracesSampleRate: 0,
            beforeSend: (event: ErrorEvent) => redigiEvento(event),
            ignoreErrors: ERRORI_IGNORATI,
            denyUrls: URL_NEGATE,
          })
          crashTestSeRichiesto()
        })
        .catch(() => {
          // chunk non scaricabile (offline, deploy a meta'): l'app parte lo stesso
        })
    },
  })
}
